import { useMemo } from 'react';
import type { components } from '@/api/schema';
import { localId, type Operation } from './outbox';
import { useSavedWork } from './useSavedWork';

/**
 * Units, categories, suppliers and items added on this PC and not yet made by
 * the server (.docs/023), shown in every list and picker beside the real ones,
 * so the stock work typed next can use them at once.
 *
 * Each stands in under a local id (`local:<operation id>`). Work that names it
 * waits for it to be made, and is sent with the server's id in its place. A
 * code is the server's to give, so until then it has none.
 */

export type PendingKind = 'uom.create' | 'category.create' | 'supplier.create' | 'sku.create';

/**
 * Shown in place of a code the server has not given yet — in a list's Code
 * column, and beside the name in every picker. The same words as a document
 * saved on this PC shows for its number.
 */
export const NO_CODE_YET = 'Not sent yet';

type UomRow = components['schemas']['UomDto'];
type CategoryRow = components['schemas']['SkuCategoryDto'];
type SupplierRow = components['schemas']['SupplierDto'];
type SkuRow = components['schemas']['SkuListItemDto'];

/** What the item form keeps beside a new item, for showing it before it exists. */
export interface PendingSkuForm {
  categoryLabel: string;
  uomLabel: string;
  uomPrecision: number;
  /** The unit one of the item's unit holds, as it was named when chosen. */
  contentUomLabel?: string | undefined;
  contentUomPrecision?: number | undefined;
}

/** Marks a row as made on this PC, not by the server. */
export type Pending<T> = T & { pending?: true };

/** Work of `kind` still to be made by the server — waiting, or refused and needing a look. */
export function pendingOps(ops: readonly Operation[], kind: PendingKind): Operation[] {
  return ops.filter((op) => op.kind === kind && op.state !== 'sent');
}

/** `{op:<id>}` tokens in a saved body back to the local ids the screens use. */
function asLocal(id: unknown): string {
  const text = String(id ?? '');
  const token = /^\{op:(.+)\}$/.exec(text);
  return token ? localId(token[1]!) : text;
}

type Body = Record<string, unknown>;
const bodyOf = (op: Operation) => (op.body ?? {}) as Body;
const text = (v: unknown) => (typeof v === 'string' && v ? v : null);

export function pendingUom(op: Operation): Pending<UomRow> {
  const b = bodyOf(op);
  return {
    id: localId(op.id),
    code: NO_CODE_YET,
    nameEn: String(b.nameEn ?? ''),
    nameUr: text(b.nameUr),
    abbreviation: text(b.abbreviation),
    precision: Number(b.precision ?? 0),
    isActive: true,
    needsReview: Boolean(b.needsReview),
    skuCount: 0,
    pending: true,
  } as Pending<UomRow>;
}

export function pendingCategory(op: Operation): Pending<CategoryRow> {
  const b = bodyOf(op);
  return {
    id: localId(op.id),
    code: NO_CODE_YET,
    nameEn: String(b.nameEn ?? ''),
    nameUr: text(b.nameUr),
    isActive: true,
    isFeed: Boolean(b.isFeed),
    needsReview: Boolean(b.needsReview),
    skuCount: 0,
    pending: true,
  } as Pending<CategoryRow>;
}

export function pendingSupplier(op: Operation): Pending<SupplierRow> {
  const b = bodyOf(op);
  return {
    id: localId(op.id),
    code: NO_CODE_YET,
    nameEn: String(b.nameEn ?? ''),
    nameUr: text(b.nameUr),
    contactPerson: text(b.contactPerson),
    contactNo: text(b.contactNo),
    altContactNo: text(b.altContactNo),
    email: text(b.email),
    address: text(b.address),
    city: text(b.city),
    ntn: text(b.ntn),
    strn: text(b.strn),
    creditDays: b.creditDays ?? null,
    creditLimit: b.creditLimit ?? null,
    notes: text(b.notes),
    isActive: true,
    pending: true,
  } as Pending<SupplierRow>;
}

export function pendingSku(op: Operation): Pending<SkuRow> {
  const b = bodyOf(op);
  const form = (op.form ?? {}) as Partial<PendingSkuForm>;
  return {
    id: localId(op.id),
    code: text(b.code) ?? NO_CODE_YET,
    nameEn: String(b.nameEn ?? ''),
    nameUr: text(b.nameUr),
    categoryId: asLocal(b.categoryId),
    category: form.categoryLabel ?? '',
    categoryCode: '',
    uomId: asLocal(b.uomId),
    uom: form.uomLabel ?? '',
    uomPrecision: form.uomPrecision ?? 2,
    contentQty: text(b.contentQty),
    contentUomId: text(b.contentUomId),
    contentUom: form.contentUomLabel ?? null,
    contentUomPrecision: form.contentUomPrecision ?? null,
    // Packages are only ever sent whole with the item, so a stand-in id each.
    packagings: (Array.isArray(b.packagings) ? (b.packagings as Record<string, unknown>[]) : []).map((p, i, all) => ({
      id: `${localId(op.id)}-${i}`,
      name: String(p.name ?? ''),
      qtyPerPackage: String(p.qtyPerPackage ?? ''),
      innerName: text(p.innerName),
      innerCount: typeof p.innerCount === 'number' ? p.innerCount : null,
      innerQty: text(p.innerQty),
      isDefault: all.some((q) => q.isDefault) ? Boolean(p.isDefault) : i === 0,
    })),
    isBatchTracked: Boolean(b.isBatchTracked),
    hasExpiry: Boolean(b.hasExpiry),
    reorderLevel: text(b.reorderLevel),
    expiryWarnDays: typeof b.expiryWarnDays === 'number' ? b.expiryWarnDays : null,
    isActive: true,
    pending: true,
  } as Pending<SkuRow>;
}

const ROWS = {
  'uom.create': pendingUom,
  'category.create': pendingCategory,
  'supplier.create': pendingSupplier,
  'sku.create': pendingSku,
} as const;

type RowOf<K extends PendingKind> = ReturnType<(typeof ROWS)[K]>;

/** The pending entries of one kind, as rows like the server's, newest last. */
export function pendingRows<K extends PendingKind>(ops: readonly Operation[], kind: K): RowOf<K>[] {
  return pendingOps(ops, kind).map((op) => ROWS[kind](op) as RowOf<K>);
}

/** `pendingRows` over the work saved on this PC, kept in step as it changes. */
export function usePendingRows<K extends PendingKind>(kind: K): RowOf<K>[] {
  const { data } = useSavedWork();
  return useMemo(() => pendingRows(data ?? [], kind), [data, kind]);
}

/** `rows` from the server with what is waiting on this PC after them. */
export function withPending<T extends { id: string }>(rows: readonly T[] | undefined, pending: readonly T[]): T[] | undefined {
  if (!pending.length) return rows as T[] | undefined;
  return [...(rows ?? []), ...pending];
}

/** True for a row that stands for something not yet made by the server. */
export function isPendingRow(row: { id: string }): boolean {
  return row.id.startsWith('local:');
}
